import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Divider,
  Chip
} from "@material-ui/core";
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  VolumeUp as VolumeUpIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Phone as PhoneIcon,
  ChatBubble as ChatIcon,
  Assignment as AssignmentIcon
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import useSettings from "../../hooks/useSettings";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useFormattingSettings from "../../hooks/useFormattingSettings";
import { getCurrencyOptions, getDateFormatOptions } from "../../utils/currencyData";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: `1px solid ${theme.palette.type === 'dark' ? '#555555' : '#e0e0e0'}`,
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  cardSection: {
    marginBottom: '16px',
    backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff'
  },
  statusChip: {
    marginLeft: '8px'
  }
}));

// Componentes customizados seguindo padrão IA.js
const SettingSwitch = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  disabled = false,
  icon,
  loading = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.checked ? "enabled" : "disabled");
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip
          icon={value === "enabled" ? <CheckCircleIcon /> : <BlockIcon />}
          label={value === "enabled" ? i18n.t("settings.settings.options.enabled") : i18n.t("settings.settings.options.disabled")}
          color={value === "enabled" ? "primary" : "default"}
          size="small"
          className={classes.statusChip}
        />
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value === "enabled"}
          onChange={handleChange}
          disabled={disabled || loading}
          style={{ 
            marginRight: '8px',
            accentColor: theme.palette.primary.main
          }}
        />
        <Typography variant="body2">
          {loading ? i18n.t("settings.settings.options.updating") : (value === "enabled" ? i18n.t("settings.settings.options.enabled") : i18n.t("settings.settings.options.disabled"))}
        </Typography>
      </label>
    </Box>
  );
});

const SettingSelect = React.memo(({
  id,
  title,
  description,
  value,
  options,
  onChange,
  icon,
  disabled = false,
  loading = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || loading}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${theme.palette.type === 'dark' ? '#555555' : '#e0e0e0'}`,
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit',
          backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
          color: theme.palette.type === 'dark' ? '#ffffff' : '#000000'
        }}
      >
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            style={{
              backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
              color: theme.palette.type === 'dark' ? '#ffffff' : '#000000'
            }}
          >
            {option.label}
          </option>
        ))}
      </select>
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
          {i18n.t("settings.settings.options.updating")}
        </Typography>
      )}
    </Box>
  );
});

const SettingTextField = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  icon,
  type = "text",
  multiline = false,
  rows = 1,
  disabled = false,
  loading = false,
  secure = false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {secure && (
          <Chip
            icon={<SecurityIcon />}
            label={i18n.t("settings.settings.options.secure")}
            color="secondary"
            size="small"
            className={classes.statusChip}
          />
        )}
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={handleChange}
          disabled={disabled || loading}
          rows={rows}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: `1px solid ${theme.palette.type === 'dark' ? '#555555' : '#e0e0e0'}`,
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'vertical',
            backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
            color: theme.palette.type === 'dark' ? '#ffffff' : '#000000'
          }}
        />
      ) : (
        <input
          type={secure ? "password" : type}
          value={value || ""}
          onChange={handleChange}
          disabled={disabled || loading}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: `1px solid ${theme.palette.type === 'dark' ? '#555555' : '#e0e0e0'}`,
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit',
            backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
            color: theme.palette.type === 'dark' ? '#ffffff' : '#000000'
          }}
        />
      )}
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
          {i18n.t("settings.settings.options.updating")}
        </Typography>
      )}
    </Box>
  );
});

export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user } = props;
  const classes = useStyles();
  const theme = useTheme();

  // useRef para evitar re-render infinito
  const isInitializedRef = useRef(false);

  // Estados organizados por seção
  const [generalSettings, setGeneralSettings] = useState({
    userCreation: "disabled",
    userRating: "disabled",
    scheduleType: "disabled",
    chatBotType: "text",
    sendGreetingAccepted: "enabled",
    userRandom: "enabled",
    settingsTransfTicket: "enabled",
    acceptCallWhatsapp: "enabled",
    sendSignMessage: "enabled",
    sendGreetingMessageOneQueues: "enabled",
    sendQueuePosition: "enabled",
    sendFarewellWaitingTicket: "enabled",
    acceptAudioMessageContact: "enabled",
    showNotificationPending: false,
    requiredTag: "enabled",
    closeTicketOnTransfer: false,
    directTicketsToWallets: false,
    alwaysSelectQueueOnAccept: "disabled",
    copyContactPrefix: "",
    informarValorVenda: false
  });

  const [paymentSettings, setPaymentSettings] = useState({
    eficlientid: '',
    eficlientsecret: '',
    efichavepix: '',
    mpaccesstoken: '',
    stripeprivatekey: '',
    asaastoken: '',
    openaitoken: ''
  });

  const [lgpdSettings, setLgpdSettings] = useState({
    enableLGPD: "disabled",
    lgpdMessage: "",
    lgpdLink: "",
    lgpdDeleteMessage: "disabled",
    lgpdConsent: "disabled",
    lgpdHideNumber: "disabled"
  });

  const [customMessages, setCustomMessages] = useState({
    transferMessage: "",
    greetingAcceptedMessage: "",
    greetingMessageOneQueuesMessage: "",
    AcceptCallWhatsappMessage: "",
    sendQueuePositionMessage: ""
  });

  const [expandedSections, setExpandedSections] = useState({
    general: true,
    payment: false,
    lgpd: false,
    system: false,
    messages: false,
    formatting: false,
    ai: false
  });

  const [loading, setLoading] = useState({});

  const { update } = useSettings();
  const { update: updateCompanySettings } = useCompanySettings();
  const {
    currency,
    dateFormat,
    loading: loadingFormattingSettings,
    updateCurrency,
    updateDateFormat
  } = useFormattingSettings();

  // Função para atualizar configurações - memoizada com useCallback
  const handleSettingChange = useCallback(async (key, value) => {
    console.log(`\n========================================`);
    console.log(`🔄 [OPTIONS] INÍCIO DA ATUALIZAÇÃO`);
    console.log(`========================================`);
    console.log(`📝 [OPTIONS] Key: ${key}`);
    console.log(`📝 [OPTIONS] Value: ${JSON.stringify(value)}`);
    console.log(`📝 [OPTIONS] Value Type: ${typeof value}`);
    console.log(`📝 [OPTIONS] Timestamp: ${new Date().toISOString()}`);
    
    // Log do estado atual antes da atualização
    const currentValue = generalSettings[key] || paymentSettings[key] || lgpdSettings[key] || customMessages[key];
    console.log(`📊 [OPTIONS] Valor atual no estado local: ${JSON.stringify(currentValue)}`);
    console.log(`📊 [OPTIONS] Mudança: ${JSON.stringify(currentValue)} → ${JSON.stringify(value)}`);
    
    setLoading(prev => ({ ...prev, [key]: true }));
    console.log(`⏳ [OPTIONS] Loading state ativado para: ${key}`);

    try {
      console.log(`\n📤 [OPTIONS] CHAMANDO API`);
      console.log(`📤 [OPTIONS] Endpoint: PUT /settings/${key}`);
      console.log(`📤 [OPTIONS] Payload:`, { key, value });
      
      const response = await update({ key, value });
      
      console.log(`\n📥 [OPTIONS] RESPOSTA DA API RECEBIDA`);
      console.log(`📥 [OPTIONS] Response completo:`, response);
      console.log(`📥 [OPTIONS] Response.id:`, response?.id);
      console.log(`📥 [OPTIONS] Response.key:`, response?.key);
      console.log(`📥 [OPTIONS] Response.value:`, response?.value);
      console.log(`📥 [OPTIONS] Response.companyId:`, response?.companyId);

      // Backend retorna o objeto Setting com id, key, value
      if (response && (response.id || response.key)) {
        console.log(`✅ [OPTIONS] Resposta válida recebida`);
        console.log(`✅ [OPTIONS] Validando valor retornado: ${JSON.stringify(response.value)} === ${JSON.stringify(value)}`);
        
        if (response.value !== value) {
          console.warn(`⚠️ [OPTIONS] ATENÇÃO: Valor retornado difere do enviado!`);
          console.warn(`⚠️ [OPTIONS] Enviado: ${JSON.stringify(value)}`);
          console.warn(`⚠️ [OPTIONS] Retornado: ${JSON.stringify(response.value)}`);
        }
        
        console.log(`\n🔄 [OPTIONS] ATUALIZANDO ESTADO LOCAL`);
        
        // Atualizar estado local baseado na seção
        let stateUpdated = false;
        
        setGeneralSettings(prev => {
          if (Object.keys(prev).includes(key)) {
            console.log(`📝 [OPTIONS] Atualizando generalSettings.${key}`);
            console.log(`📝 [OPTIONS] Valor anterior: ${JSON.stringify(prev[key])}`);
            console.log(`📝 [OPTIONS] Novo valor: ${JSON.stringify(value)}`);
            stateUpdated = true;
            return { ...prev, [key]: value };
          }
          return prev;
        });

        setPaymentSettings(prev => {
          if (Object.keys(prev).includes(key)) {
            console.log(`📝 [OPTIONS] Atualizando paymentSettings.${key}`);
            console.log(`📝 [OPTIONS] Valor anterior: ${JSON.stringify(prev[key])}`);
            console.log(`📝 [OPTIONS] Novo valor: ${JSON.stringify(value)}`);
            stateUpdated = true;
            return { ...prev, [key]: value };
          }
          return prev;
        });

        setLgpdSettings(prev => {
          if (Object.keys(prev).includes(key)) {
            console.log(`📝 [OPTIONS] Atualizando lgpdSettings.${key}`);
            console.log(`📝 [OPTIONS] Valor anterior: ${JSON.stringify(prev[key])}`);
            console.log(`📝 [OPTIONS] Novo valor: ${JSON.stringify(value)}`);
            stateUpdated = true;
            return { ...prev, [key]: value };
          }
          return prev;
        });

        setCustomMessages(prev => {
          if (Object.keys(prev).includes(key)) {
            console.log(`📝 [OPTIONS] Atualizando customMessages.${key}`);
            console.log(`📝 [OPTIONS] Valor anterior: ${JSON.stringify(prev[key])}`);
            console.log(`📝 [OPTIONS] Novo valor: ${JSON.stringify(value)}`);
            stateUpdated = true;
            return { ...prev, [key]: value };
          }
          return prev;
        });
        
        if (stateUpdated) {
          console.log(`✅ [OPTIONS] Estado local atualizado com sucesso`);
        } else {
          console.warn(`⚠️ [OPTIONS] ATENÇÃO: Key '${key}' não encontrada em nenhum estado!`);
        }

        // Toast de sucesso
        console.log(`\n📢 [OPTIONS] Exibindo toast de sucesso`);
        toastUtils.success(
          i18n.t("settings.settings.options.success"),
          i18n.t("settings.settings.options.updateSuccess")
        );
        
        console.log(`\n✅ [OPTIONS] ATUALIZAÇÃO CONCLUÍDA COM SUCESSO`);
        console.log(`========================================\n`);
      } else {
        console.error(`\n❌ [OPTIONS] RESPOSTA INVÁLIDA DO SERVIDOR`);
        console.error(`❌ [OPTIONS] Response recebido:`, response);
        console.error(`❌ [OPTIONS] Esperado: objeto com 'id' ou 'key'`);
        console.error(`========================================\n`);
        throw new Error("Resposta inválida do servidor");
      }

    } catch (error) {
      console.error(`\n❌ [OPTIONS] ERRO AO ATUALIZAR CONFIGURAÇÃO`);
      console.error(`========================================`);
      console.error(`❌ [OPTIONS] Key: ${key}`);
      console.error(`❌ [OPTIONS] Value: ${JSON.stringify(value)}`);
      console.error(`❌ [OPTIONS] Error object:`, error);
      console.error(`❌ [OPTIONS] Error message:`, error?.message);
      console.error(`❌ [OPTIONS] Error name:`, error?.name);
      console.error(`❌ [OPTIONS] Error stack:`, error?.stack);
      
      if (error?.response) {
        console.error(`❌ [OPTIONS] HTTP Response Error:`);
        console.error(`❌ [OPTIONS] Status:`, error.response.status);
        console.error(`❌ [OPTIONS] Status Text:`, error.response.statusText);
        console.error(`❌ [OPTIONS] Data:`, error.response.data);
        console.error(`❌ [OPTIONS] Headers:`, error.response.headers);
      } else if (error?.request) {
        console.error(`❌ [OPTIONS] Network Error - No response received`);
        console.error(`❌ [OPTIONS] Request:`, error.request);
      }
      
      console.error(`========================================\n`);

      // Toast de erro
      const errorMessage = error?.response?.data?.message || error?.message || i18n.t("settings.settings.options.updateError");
      console.log(`📢 [OPTIONS] Exibindo toast de erro: ${errorMessage}`);

      toastUtils.error(
        i18n.t("settings.settings.options.error"),
        errorMessage
      );

      // Reverter mudança no estado local se falhou
      console.log(`🔄 [OPTIONS] Revertendo estados locais (forçando re-render)`);
      setGeneralSettings(prev => ({ ...prev }));
      setPaymentSettings(prev => ({ ...prev }));
      setLgpdSettings(prev => ({ ...prev }));
      setCustomMessages(prev => ({ ...prev }));
    } finally {
      console.log(`⏳ [OPTIONS] Desativando loading state para: ${key}`);
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [update]);

  const handleCurrencyChange = useCallback(async (key, value) => {
    console.log(`🔄 Atualizando moeda: ${value}`);
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      await updateCurrency(value);
      console.log(`✅ Moeda atualizada com sucesso`);

      // Toast de sucesso
      toastUtils.success(
        i18n.t("settings.settings.options.success"),
        i18n.t("settings.settings.options.currencyUpdateSuccess")
      );
    } catch (error) {
      console.error(`❌ Erro ao atualizar moeda:`, error);

      // Toast de erro
      const errorMessage = error?.response?.data?.message || i18n.t("settings.settings.options.updateError");
      toastUtils.error(
        i18n.t("settings.settings.options.error"),
        errorMessage
      );
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [updateCurrency]);

  const handleDateFormatChange = useCallback(async (key, value) => {
    console.log(`🔄 Atualizando formato de data: ${value}`);
    setLoading(prev => ({ ...prev, [key]: true }));
    try {
      await updateDateFormat(value);
      console.log(`✅ Formato de data atualizado com sucesso`);

      // Toast de sucesso
      toastUtils.success(
        i18n.t("settings.settings.options.success"),
        i18n.t("settings.settings.options.dateFormatUpdateSuccess")
      );
    } catch (error) {
      console.error(`❌ Erro ao atualizar formato de data:`, error);

      // Toast de erro
      const errorMessage = error?.response?.data?.message || i18n.t("settings.settings.options.updateError");
      toastUtils.error(
        i18n.t("settings.settings.options.error"),
        errorMessage
      );
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [updateDateFormat]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Carregar configurações iniciais - CORRIGIDO para evitar loop infinito
  useEffect(() => {
    // Evitar re-processamento se já foi inicializado e oldSettings não mudou
    if (!Array.isArray(oldSettings) || oldSettings.length === 0) {
      return;
    }

    // Prevenir múltiplas inicializações desnecessárias
    if (isInitializedRef.current) {
      return;
    }

    const initialGeneralSettings = {
      userCreation: "disabled",
      userRating: "disabled",
      scheduleType: "disabled",
      chatBotType: "text",
      sendGreetingAccepted: "enabled",
      userRandom: "enabled",
      settingsTransfTicket: "enabled",
      acceptCallWhatsapp: "enabled",
      sendSignMessage: "enabled",
      sendGreetingMessageOneQueues: "enabled",
      sendQueuePosition: "enabled",
      sendFarewellWaitingTicket: "enabled",
      acceptAudioMessageContact: "enabled",
      showNotificationPending: false,
      requiredTag: "enabled",
      closeTicketOnTransfer: false,
      directTicketsToWallets: false,
      alwaysSelectQueueOnAccept: "disabled",
      copyContactPrefix: "",
      informarValorVenda: false
    };

    const initialPaymentSettings = {
      eficlientid: '',
      eficlientsecret: '',
      efichavepix: '',
      mpaccesstoken: '',
      stripeprivatekey: '',
      asaastoken: '',
      openaitoken: ''
    };

    const initialLgpdSettings = {
      enableLGPD: "disabled",
      lgpdMessage: "",
      lgpdLink: "",
      lgpdDeleteMessage: "disabled",
      lgpdConsent: "disabled",
      lgpdHideNumber: "disabled"
    };

    const initialCustomMessages = {
      transferMessage: "",
      greetingAcceptedMessage: "",
      greetingMessageOneQueuesMessage: "",
      AcceptCallWhatsappMessage: "",
      sendQueuePositionMessage: ""
    };

    const newGeneralSettings = { ...initialGeneralSettings };
    const newPaymentSettings = { ...initialPaymentSettings };
    const newLgpdSettings = { ...initialLgpdSettings };
    const newCustomMessages = { ...initialCustomMessages };

    oldSettings.forEach(setting => {
      if (Object.keys(initialGeneralSettings).includes(setting.key)) {
        // Tratamento especial para valores booleanos
        if (['showNotificationPending', 'closeTicketOnTransfer', 'directTicketsToWallets', 'informarValorVenda'].includes(setting.key)) {
          // Converter string "true"/"false" para booleano
          newGeneralSettings[setting.key] = setting.value === true || setting.value === "true";
        } else {
          // Para valores não booleanos, usar diretamente
          newGeneralSettings[setting.key] = setting.value;
        }
      } else if (Object.keys(initialPaymentSettings).includes(setting.key)) {
        newPaymentSettings[setting.key] = setting.value;
      } else if (Object.keys(initialLgpdSettings).includes(setting.key)) {
        newLgpdSettings[setting.key] = setting.value;
      } else if (Object.keys(initialCustomMessages).includes(setting.key)) {
        newCustomMessages[setting.key] = setting.value;
      }
    });

    setGeneralSettings(newGeneralSettings);
    setPaymentSettings(newPaymentSettings);
    setLgpdSettings(newLgpdSettings);
    setCustomMessages(newCustomMessages);

    // Marcar como inicializado
    isInitializedRef.current = true;
  }, [oldSettings]);

  const isSuper = () => user?.super;

  const scheduleOptions = [
    { value: "disabled", label: i18n.t("settings.settings.options.disabled") },
    { value: "queue", label: i18n.t("settings.settings.options.queueManagement") },
    { value: "company", label: i18n.t("settings.settings.options.companyManagement") },
    { value: "connection", label: i18n.t("settings.settings.options.connectionManagement") }
  ];

  const chatBotOptions = [
    { value: "text", label: i18n.t("settings.settings.options.textOnly") },
    { value: "voice", label: i18n.t("settings.settings.options.voiceAndText") }
  ];

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <SettingsIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("settings.settings.options.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("settings.settings.options.description")}
        </Typography>
      </Box>

      {/* Configurações Gerais */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t("settings.settings.options.generalSettings")}
          action={
            <IconButton onClick={() => toggleSection('general')}>
              {expandedSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.general}>
          <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userCreation"
                  title={i18n.t("settings.settings.options.creationCompanyUser")}
                  description={i18n.t("settings.settings.options.creationCompanyUserDesc")}
                  value={generalSettings.userCreation}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.userCreation}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userRating"
                  title={i18n.t("settings.settings.options.evaluations")}
                  description={i18n.t("settings.settings.options.evaluationsDesc")}
                  value={generalSettings.userRating}
                  onChange={handleSettingChange}
                  icon={<StarIcon className={classes.sectionIcon} />}
                  loading={loading.userRating}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="scheduleType"
                  title={i18n.t("settings.settings.options.officeScheduling")}
                  description={i18n.t("settings.settings.options.officeSchedulingDesc")}
                  value={generalSettings.scheduleType}
                  options={scheduleOptions}
                  onChange={handleSettingChange}
                  icon={<ScheduleIcon className={classes.sectionIcon} />}
                  loading={loading.scheduleType}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendGreetingAccepted"
                  title={i18n.t("settings.settings.options.sendGreetingAccepted")}
                  description={i18n.t("settings.settings.options.sendGreetingAcceptedDesc")}
                  value={generalSettings.sendGreetingAccepted}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendGreetingAccepted}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="userRandom"
                  title={i18n.t("settings.settings.options.userRandom")}
                  description={i18n.t("settings.settings.options.userRandomDesc")}
                  value={generalSettings.userRandom}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.userRandom}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="acceptCallWhatsapp"
                  title={i18n.t("settings.settings.options.acceptCallWhatsapp")}
                  description={i18n.t("settings.settings.options.acceptCallWhatsappDesc")}
                  value={generalSettings.acceptCallWhatsapp}
                  onChange={handleSettingChange}
                  icon={<PhoneIcon className={classes.sectionIcon} />}
                  loading={loading.acceptCallWhatsapp}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendSignMessage"
                  title={i18n.t("settings.settings.options.sendSignMessage")}
                  description={i18n.t("settings.settings.options.sendSignMessageDesc")}
                  value={generalSettings.sendSignMessage}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendSignMessage}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendGreetingMessageOneQueues"
                  title={i18n.t("settings.settings.options.sendGreetingMessageOneQueues")}
                  description={i18n.t("settings.settings.options.sendGreetingMessageOneQueuesDesc")}
                  value={generalSettings.sendGreetingMessageOneQueues}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendGreetingMessageOneQueues}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendQueuePosition"
                  title={i18n.t("settings.settings.options.sendQueuePosition")}
                  description={i18n.t("settings.settings.options.sendQueuePositionDesc")}
                  value={generalSettings.sendQueuePosition}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.sendQueuePosition}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="sendFarewellWaitingTicket"
                  title={i18n.t("settings.settings.options.sendFarewellWaitingTicket")}
                  description={i18n.t("settings.settings.options.sendFarewellWaitingTicketDesc")}
                  value={generalSettings.sendFarewellWaitingTicket}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.sendFarewellWaitingTicket}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="acceptAudioMessageContact"
                  title={i18n.t("settings.settings.options.acceptAudioMessageContact")}
                  description={i18n.t("settings.settings.options.acceptAudioMessageContactDesc")}
                  value={generalSettings.acceptAudioMessageContact}
                  onChange={handleSettingChange}
                  icon={<VolumeUpIcon className={classes.sectionIcon} />}
                  loading={loading.acceptAudioMessageContact}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="settingsTransfTicket"
                  title={i18n.t("settings.settings.options.settingsTransfTicket")}
                  description={i18n.t("settings.settings.options.settingsTransfTicketDesc")}
                  value={generalSettings.settingsTransfTicket}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.settingsTransfTicket}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="chatBotType"
                  title={i18n.t("settings.settings.options.chatBotType")}
                  description={i18n.t("settings.settings.options.chatBotTypeDesc")}
                  value={generalSettings.chatBotType}
                  options={chatBotOptions}
                  onChange={handleSettingChange}
                  icon={<ChatIcon className={classes.sectionIcon} />}
                  loading={loading.chatBotType}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="informarValorVenda"
                  title={i18n.t("settings.settings.options.informarValorVenda")}
                  description={i18n.t("settings.settings.options.informarValorVendaDesc")}
                  value={generalSettings.informarValorVenda ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<MoneyIcon className={classes.sectionIcon} />}
                  loading={loading.informarValorVenda}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Pagamento */}
      {isSuper() && (
        <Card className={classes.cardSection}>
          <CardHeader
            avatar={<PaymentIcon />}
            title={i18n.t("settings.settings.options.paymentSettings")}
            action={
              <IconButton onClick={() => toggleSection('payment')}>
                {expandedSections.payment ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.payment}>
            <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="eficlientid"
                    title={i18n.t("settings.settings.options.efiClientId")}
                    description={i18n.t("settings.settings.options.efiClientIdDesc")}
                    value={paymentSettings.eficlientid}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.eficlientid}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="eficlientsecret"
                    title={i18n.t("settings.settings.options.efiClientSecret")}
                    description={i18n.t("settings.settings.options.efiClientSecretDesc")}
                    value={paymentSettings.eficlientsecret}
                    onChange={handleSettingChange}
                    icon={<SecurityIcon className={classes.sectionIcon} />}
                    loading={loading.eficlientsecret}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="mpaccesstoken"
                    title={i18n.t("settings.settings.options.mpAccessToken")}
                    description={i18n.t("settings.settings.options.mpAccessTokenDesc")}
                    value={paymentSettings.mpaccesstoken}
                    onChange={handleSettingChange}
                    icon={<MoneyIcon className={classes.sectionIcon} />}
                    loading={loading.mpaccesstoken}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="asaastoken"
                    title={i18n.t("settings.settings.options.asaasToken")}
                    description={i18n.t("settings.settings.options.asaasTokenDesc")}
                    value={paymentSettings.asaastoken}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.asaastoken}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="efichavepix"
                    title={i18n.t("settings.settings.options.efiChavePix")}
                    description={i18n.t("settings.settings.options.efiChavePixDesc")}
                    value={paymentSettings.efichavepix}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.efichavepix}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="stripeprivatekey"
                    title={i18n.t("settings.settings.options.stripePrivateKey")}
                    description={i18n.t("settings.settings.options.stripePrivateKeyDesc")}
                    value={paymentSettings.stripeprivatekey}
                    onChange={handleSettingChange}
                    icon={<PaymentIcon className={classes.sectionIcon} />}
                    loading={loading.stripeprivatekey}
                    secure
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SettingTextField
                    id="openaitoken"
                    title={i18n.t("settings.settings.options.openAiToken")}
                    description={i18n.t("settings.settings.options.openAiTokenDesc")}
                    value={paymentSettings.openaitoken}
                    onChange={handleSettingChange}
                    icon={<SecurityIcon className={classes.sectionIcon} />}
                    loading={loading.openaitoken}
                    secure
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Configurações de LGPD */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SecurityIcon />}
          title={i18n.t("settings.settings.options.lgpdSettings")}
          action={
            <IconButton onClick={() => toggleSection('lgpd')}>
              {expandedSections.lgpd ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.lgpd}>
          <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="enableLGPD"
                  title={i18n.t("settings.settings.options.enableLGPD")}
                  description={i18n.t("settings.settings.options.enableLGPDDesc")}
                  value={lgpdSettings.enableLGPD}
                  onChange={handleSettingChange}
                  icon={<SecurityIcon className={classes.sectionIcon} />}
                  loading={loading.enableLGPD}
                />
              </Grid>

              {lgpdSettings.enableLGPD === "enabled" && (
                <>
                  <Grid item xs={12}>
                    <SettingTextField
                      id="lgpdMessage"
                      title={i18n.t("settings.settings.options.lgpdMessage")}
                      description={i18n.t("settings.settings.options.lgpdMessageDesc")}
                      value={lgpdSettings.lgpdMessage}
                      onChange={handleSettingChange}
                      icon={<MessageIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdMessage}
                      multiline
                      rows={4}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingTextField
                      id="lgpdLink"
                      title={i18n.t("settings.settings.options.lgpdLink")}
                      description={i18n.t("settings.settings.options.lgpdLinkDesc")}
                      value={lgpdSettings.lgpdLink}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdLink}
                      type="url"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdDeleteMessage"
                      title={i18n.t("settings.settings.options.lgpdDeleteMessage")}
                      description={i18n.t("settings.settings.options.lgpdDeleteMessageDesc")}
                      value={lgpdSettings.lgpdDeleteMessage}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdDeleteMessage}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdConsent"
                      title={i18n.t("settings.settings.options.lgpdConsent")}
                      description={i18n.t("settings.settings.options.lgpdConsentDesc")}
                      value={lgpdSettings.lgpdConsent}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdConsent}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSwitch
                      id="lgpdHideNumber"
                      title={i18n.t("settings.settings.options.lgpdHideNumber")}
                      description={i18n.t("settings.settings.options.lgpdHideNumberDesc")}
                      value={lgpdSettings.lgpdHideNumber}
                      onChange={handleSettingChange}
                      icon={<SecurityIcon className={classes.sectionIcon} />}
                      loading={loading.lgpdHideNumber}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Formatação */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<DateRangeIcon />}
          title={i18n.t("settings.settings.options.formattingSettings")}
          action={
            <IconButton onClick={() => toggleSection('formatting')}>
              {expandedSections.formatting ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.formatting}>
          <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="systemCurrency"
                  title={i18n.t("settings.settings.options.systemCurrency")}
                  description={i18n.t("settings.settings.options.systemCurrencyDesc")}
                  value={currency}
                  options={getCurrencyOptions()}
                  onChange={handleCurrencyChange}
                  icon={<MoneyIcon className={classes.sectionIcon} />}
                  loading={loadingFormattingSettings}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSelect
                  id="systemDateFormat"
                  title={i18n.t("settings.settings.options.systemDateFormat")}
                  description={i18n.t("settings.settings.options.systemDateFormatDesc")}
                  value={dateFormat}
                  options={getDateFormatOptions()}
                  onChange={handleDateFormatChange}
                  icon={<DateRangeIcon className={classes.sectionIcon} />}
                  loading={loadingFormattingSettings}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Sistema */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t("settings.settings.options.systemSettings")}
          action={
            <IconButton onClick={() => toggleSection('system')}>
              {expandedSections.system ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.system}>
          <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="requiredTag"
                  title={i18n.t("settings.settings.options.requiredTag")}
                  description={i18n.t("settings.settings.options.requiredTagDesc")}
                  value={generalSettings.requiredTag}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.requiredTag}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="closeTicketOnTransfer"
                  title={i18n.t("settings.settings.options.closeTicketOnTransfer")}
                  description={i18n.t("settings.settings.options.closeTicketOnTransferDesc")}
                  value={generalSettings.closeTicketOnTransfer ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.closeTicketOnTransfer}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="directTicketsToWallets"
                  title={i18n.t("settings.settings.options.directTicketsToWallets")}
                  description={i18n.t("settings.settings.options.directTicketsToWalletsDesc")}
                  value={generalSettings.directTicketsToWallets ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.directTicketsToWallets}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="showNotificationPending"
                  title={i18n.t("settings.settings.options.showNotificationPending")}
                  description={i18n.t("settings.settings.options.showNotificationPendingDesc")}
                  value={generalSettings.showNotificationPending ? "enabled" : "disabled"}
                  onChange={(key, value) => handleSettingChange(key, value === "enabled")}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.showNotificationPending}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingSwitch
                  id="alwaysSelectQueueOnAccept"
                  title={i18n.t("settings.settings.options.alwaysSelectQueueOnAccept")}
                  description={i18n.t("settings.settings.options.alwaysSelectQueueOnAcceptDesc")}
                  value={generalSettings.alwaysSelectQueueOnAccept}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.alwaysSelectQueueOnAccept}
                />
              </Grid>

              <Grid item xs={12}>
                <SettingTextField
                  id="copyContactPrefix"
                  title={i18n.t("settings.settings.options.copyContactPrefix")}
                  description={i18n.t("settings.settings.options.copyContactPrefixDesc")}
                  value={generalSettings.copyContactPrefix}
                  onChange={handleSettingChange}
                  icon={<PersonIcon className={classes.sectionIcon} />}
                  loading={loading.copyContactPrefix}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Mensagens Customizadas */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<ChatIcon />}
          title={i18n.t("settings.settings.options.customMessages")}
          action={
            <IconButton onClick={() => toggleSection('messages')}>
              {expandedSections.messages ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.messages}>
          <CardContent style={{ backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="transferMessage"
                  title={i18n.t("settings.settings.options.transferMessage")}
                  description={i18n.t("settings.settings.options.transferMessageDesc")}
                  value={customMessages.transferMessage}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.transferMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="greetingAcceptedMessage"
                  title={i18n.t("settings.settings.options.greetingAcceptedMessage")}
                  description={i18n.t("settings.settings.options.greetingAcceptedMessageDesc")}
                  value={customMessages.greetingAcceptedMessage}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.greetingAcceptedMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="greetingMessageOneQueuesMessage"
                  title={i18n.t("settings.settings.options.greetingMessageOneQueuesMessage")}
                  description={i18n.t("settings.settings.options.greetingMessageOneQueuesMessageDesc")}
                  value={customMessages.greetingMessageOneQueuesMessage}
                  onChange={handleSettingChange}
                  icon={<MessageIcon className={classes.sectionIcon} />}
                  loading={loading.greetingMessageOneQueuesMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="AcceptCallWhatsappMessage"
                  title={i18n.t("settings.settings.options.acceptCallWhatsappMessage")}
                  description={i18n.t("settings.settings.options.acceptCallWhatsappMessageDesc")}
                  value={customMessages.AcceptCallWhatsappMessage}
                  onChange={handleSettingChange}
                  icon={<PhoneIcon className={classes.sectionIcon} />}
                  loading={loading.AcceptCallWhatsappMessage}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SettingTextField
                  id="sendQueuePositionMessage"
                  title={i18n.t("settings.settings.options.sendQueuePositionMessage")}
                  description={i18n.t("settings.settings.options.sendQueuePositionMessageDesc")}
                  value={customMessages.sendQueuePositionMessage}
                  onChange={handleSettingChange}
                  icon={<AssignmentIcon className={classes.sectionIcon} />}
                  loading={loading.sendQueuePositionMessage}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
}
